import { FC, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Alert, Box, Dialog, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { getPartners, patchPartner, postPartner } from 'entities/Partners/api';
import { GeneralPartnerType, PartnerType } from 'entities/Partners/types';
import { PartnersEnum } from 'entities/Partners/enums';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { SelectOption } from 'shared/ui/Select/types';
import { InputField } from 'shared/ui/InputField';
import { ColorPalette } from 'shared/consts/colorPalette';
import { FormSelect } from 'shared/ui/Select/FormSelect';
import { Paper } from 'shared/ui/Paper';
import { capitalizeFirstLetter } from 'shared/helpers/capitalizeFirstLetter';
import { PartnersValidationSchema } from 'widgets/Partners/consts/validation';
import { PartnersFormType } from 'widgets/Partners/types';
import { Button } from 'shared/ui/Button';
import FormAutocomplete from 'shared/ui/autocomplete/form-autocomplete';
import { SmallIconButton } from 'shared/ui/SmallIconButton';
import { IconButton } from 'shared/ui/IconButton';

interface ManualModalPropsType {
	partnerToEdit?: GeneralPartnerType;
	open: boolean;
	handleClose: () => void;
}

export const ManualModal: FC<ManualModalPropsType> = ({ partnerToEdit, open, handleClose }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [duplicatedEmails, setDuplicatedEmails] = useState<string[]>([]);

	const { mutate: patchPartnerMutate, isLoading: patchIsLoading } = useMutation({
		mutationKey: ['patch/partner'],
		mutationFn: patchPartner,
	});

	const { mutate: postMutate, isLoading: postIsLoading } = useMutation({
		mutationKey: ['post/partner'],
		mutationFn: postPartner,
	});
	const disabled = postIsLoading || patchIsLoading;

	const onCreatePartnerHandler = (data: PartnerType) => {
		postMutate(
			{ data },
			{
				onSuccess: async () => {
					await queryClient.refetchQueries({ queryKey: ['get/partners'] });
					enqueueSnackbar({
						message: t('partners.snackbar.partner_created_success', {
							type: capitalizeFirstLetter(data.type),
						}),
						variant: 'success',
					});
					onCloseHandler();
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	const onUpdatePartnerHandler = (partnerId: string, data: PartnerType) => {
		patchPartnerMutate(
			{ partnerId, data },
			{
				onSuccess: async () => {
					await queryClient.refetchQueries({ queryKey: ['get/partners'] });
					enqueueSnackbar({
						message: t('partners.snackbar.partner_updated_success', {
							type: capitalizeFirstLetter(data.type),
						}),
						variant: 'success',
					});
					onCloseHandler();
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	const checkEmailDuplication = async (email: string) => {
		try {
			const response = await queryClient.fetchQuery({
				queryKey: ['get/partners', 'email-duplication-check', email],
				queryFn: () => getPartners({ type: null, search: email }),
			});
			const duplicates = response.data.results.filter(
				(partner) => partner._id !== partnerToEdit?._id && partner.emails?.includes(email),
			);
			if (duplicates.length) {
				setDuplicatedEmails((prev) => (prev.includes(email) ? prev : [...prev, email]));
			}
		} catch (err) {
			showErrorNotification(err as AxiosError);
		}
	};

	const onEmailsChangeHandler = (emails: string[], prevEmails: string[]) => {
		setDuplicatedEmails((prev) => prev.filter((email) => emails.includes(email)));
		if (emails.length > prevEmails.length) {
			const addedEmail = emails[emails.length - 1];
			if (addedEmail) {
				checkEmailDuplication(addedEmail);
			}
		}
	};

	const onCloseHandler = () => {
		setDuplicatedEmails([]);
		handleClose();
	};

	const handleManualModalSubmit = (values: PartnersFormType) => {
		const data: PartnerType = {
			...values,
			destinations: values.destinations?.length
				? (values.destinations as PartnerType['destinations'])
				: undefined,
		};
		if (partnerToEdit) {
			onUpdatePartnerHandler(partnerToEdit._id, data);
		} else {
			onCreatePartnerHandler(data);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onCloseHandler}
			PaperComponent={Paper}
			PaperProps={{
				title: (partnerToEdit ? t('shared_texts.edit') : t('shared_texts.add')) as string,
				sx: { width: '860px', maxWidth: '860px', padding: 3 },
			}}
		>
			{!partnerToEdit && (
				<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ marginBottom: '24px' }}>
					{t('partners.modals.creation_description')}
				</Typography>
			)}
			<Formik
				initialValues={{
					name: partnerToEdit?.name || '',
					vatNumber: partnerToEdit?.vatNumber || undefined,
					address: partnerToEdit?.address || undefined,
					type: partnerToEdit?.type || PartnersEnum.default,
					erpCode: partnerToEdit?.erpCode || undefined,
					note: partnerToEdit?.note || undefined,
					emails: partnerToEdit?.emails || [],
					destinations: partnerToEdit?.destinations || [],
				}}
				validationSchema={PartnersValidationSchema}
				onSubmit={handleManualModalSubmit}
			>
				{({ values, setFieldValue }) => (
					<Form>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr',
								gap: '24px',
								alignItems: 'start',
							}}
						>
							{/* LEFT COLUMN — main fields */}
							<Box>
								<InputField
									name={'name'}
									label={t('auth.labels.company_name')}
									placeholder={t('auth.validation.enter_company_name')}
									required
									disabled={disabled}
								/>
								<FormSelect
									name={'type'}
									label={t('partners.labels.client_supplier') as string}
									placeholder={t('partners.placeholder.client_supplier')}
									options={selectOptions}
									value={values.type}
									onSelect={(value) => setFieldValue('type', value)}
									required
									disabled={disabled}
								/>
								<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
									<InputField
										name={'vatNumber'}
										label={t('auth.labels.vat_number')}
										placeholder={t('auth.validation.enter_vat_number')}
										disabled={disabled}
									/>
									<InputField
										name={'erpCode'}
										label={t('partners.labels.erpCode')}
										placeholder={t('partners.placeholder.erpCode')}
										disabled={disabled}
									/>
								</Box>
								<InputField
									name={'address'}
									label={t('partners.labels.address')}
									placeholder={t('partners.placeholder.address')}
									disabled={disabled}
								/>
								<FormAutocomplete
									name="emails"
									defaultValue={partnerToEdit?.emails || []}
									options={[]}
									freeSolo
									multiple
									label={t('partners.labels.emails')}
									placeholder={t('partners.placeholder.emails') as string}
									disabled={disabled}
									onChange={(newVal) =>
										onEmailsChangeHandler(
											(newVal as unknown as string[]) || [],
											values.emails || [],
										)
									}
								/>
								{!!duplicatedEmails.length && (
									<Alert severity={'warning'} sx={{ marginBottom: '15px' }}>
										{t('partners.snackbar.email_already_in_use', {
											email: duplicatedEmails.join(', '),
										})}
									</Alert>
								)}
								<InputField
									name={'note'}
									label={t('partners.labels.note')}
									placeholder={t('partners.placeholder.note')}
									multiline
									maxRows={3}
									disabled={disabled}
								/>
							</Box>

							{/* RIGHT COLUMN — Receivers */}
							<Box sx={{ display: 'flex', flexDirection: 'column' }}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										mb: '12px',
									}}
								>
									<Typography variant={'body2'} color={ColorPalette.baliHai}>
										{t('partners.labels.receivers')}
									</Typography>
									<SmallIconButton
										iconId="add_round"
										onClick={(e) => {
											e.stopPropagation();
											setFieldValue('destinations', [
												...(values.destinations || []),
												{ receiverName: '', receiverAddress: '', receiverId: '' },
											]);
										}}
										disabled={disabled}
									/>
								</Box>

								{values.destinations?.length ? (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: '12px',
											maxHeight: '500px',
											overflowY: 'auto',
											pr: '4px',
										}}
									>
										{values.destinations.map((dest, i) => (
											<Box
												key={`destination_${i}`}
												sx={{
													border: `1px solid ${ColorPalette.baliHai}33`,
													borderRadius: '8px',
													padding: '8px 12px 4px',
												}}
											>
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
														mb: '4px',
													}}
												>
													<Typography
														variant={'caption'}
														color={ColorPalette.baliHai}
													>
														#{i + 1}
													</Typography>
													<IconButton
														iconId={'trush_icon_red'}
														sxWrapper={{
															minWidth: 'unset',
															width: '28px',
															height: '28px',
															backgroundColor: 'transparent',
															boxShadow: 'none',
														}}
														onClick={(e) => {
															e.stopPropagation();
															setFieldValue(
																'destinations',
																values.destinations?.filter(
																	(_, index) => index !== i,
																),
															);
														}}
													/>
												</Box>
												<Box sx={{ display: 'flex', gap: 2 }}>
													<InputField
														name={`destinations.${i}.receiverName`}
														label={t('partners.labels.receiver_name')}
														placeholder={t('partners.placeholder.receiver_name')}
														required
														disabled={disabled}
													/>

													<InputField
														name={`destinations.${i}.receiverId`}
														label={t('partners.labels.receiver_id')}
														placeholder={t('partners.placeholder.receiver_id')}
														required
														disabled={disabled}
													/>
												</Box>
												<InputField
													name={`destinations.${i}.receiverAddress`}
													label={t('partners.labels.receiver_address')}
													placeholder={t('partners.placeholder.receiver_address')}
													required
													disabled={disabled}
												/>
											</Box>
										))}
									</Box>
								) : (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											height: '80px',
											border: `1px dashed ${ColorPalette.baliHai}55`,
											borderRadius: '8px',
										}}
									>
										<Typography variant={'body2'} color={ColorPalette.baliHai}>
											{t('partners.labels.no_receivers')}
										</Typography>
									</Box>
								)}
							</Box>
						</Box>

						<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '16px' }}>
							<Button type={'submit'} size={'small'} disabled={disabled}>
								{partnerToEdit ? t('shared_texts.edit') : t('shared_texts.add')}
							</Button>
						</Box>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

const selectOptions: Array<SelectOption> = [
	{
		value: PartnersEnum.client,
		label: 'partners.tabs.clients',
	},
	{
		value: PartnersEnum.supplier,
		label: 'partners.tabs.suppliers',
	},
];
